import { RestRequest, RestContext, ResponseFunction } from 'msw'
import {
  addToEntityListData,
  updateEntityListData,
  deleteEntityListData,
} from '../../modules/mockDataCommon'
import { requireAuthenticatedUser } from '../../decorators'
import { _storeEntityDataListToLocalForage } from '../../modules/mockDataStorageManager'
import { getOpportunityRegistrationListData } from './mockData'
import {
  emailForPostProblem,
  queuedEmailMessage,
} from '../../modules/emailForPost'
import type {
  IOpportunityRegistrationForPost,
  IOpportunityRegistration,
} from '~/types/opportunity/models'
import { OpportunityRegistrationStatusIds } from '~/services/selectLists/opportunityRegistrationStatii'

interface GetOpportunityRegistrationsResponse {
  data: IOpportunityRegistration[]
}

async function getOpportunityRegistrations(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const responseBody: GetOpportunityRegistrationsResponse = {
    data: await getOpportunityRegistrationListData(),
  }
  return res(ctx.status(200), ctx.json(responseBody))
}

// post / patch
//
// The email rides INSIDE the registration's request, as an
// IAddressedEmailForPost on `registrationEmail`
// (IOpportunityRegistrationForPost), and comes back as the
// message it became (IEmailMessage) -- or null when the registration sent
// none. Refused with a 400 when the server would refuse it
// (emailForPostProblem), and on an edit: the email is sent by the create, once.

// Who the mock says sent it. The real server takes the logged-in team member.
const SENT_BY = { id: 'XXTM0001', label: 'Meryl Streep' }

const refuse = (res: ResponseFunction, ctx: RestContext, detail: string) =>
  res(
    ctx.status(400),
    ctx.json({ status: 400, title: 'Validation failed', detail })
  )

async function postedEmailProblem(req: RestRequest): Promise<string | null> {
  const body = (await req.json()) as { registrationEmail?: unknown }
  return body.registrationEmail === undefined
    ? null
    : emailForPostProblem(body.registrationEmail)
}

// The posted IAddressedEmailForPost, swapped for the message it became, on the stored
// row (which is also the response). Null when none was posted, as the server
// reads back a registration made manually -- and its Registration Details null
// on one that sent an email, its Approved On null until it is approved.
async function materializeRegistrationEmail({
  targetItem,
}: {
  targetItem: IOpportunityRegistration
}) {
  // Typed as the message it is about to become, the row holds the posted email.
  const email = targetItem.registrationEmail as unknown as
    | IOpportunityRegistrationForPost['registrationEmail']
    | null
  targetItem.registrationEmail = email
    ? queuedEmailMessage({
        id: `XXOREGEM${targetItem.hrid ?? targetItem.id}`,
        email,
        subjectFallback: targetItem.descr,
        sentBy: SENT_BY,
      })
    : null
  targetItem.registrationDetails ??= null
  targetItem.approvedOn ??= null
}

const postOpportunityRegistration = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  const problem = await postedEmailProblem(req)
  if (problem) {
    return refuse(res, ctx, problem)
  }
  return addToEntityListData(
    req,
    res,
    ctx,
    getOpportunityRegistrationListData,
    materializeRegistrationEmail
  )
}

const patchOpportunityRegistration = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  const body = (await req.json()) as { registrationEmail?: unknown }
  if (body.registrationEmail !== undefined) {
    return refuse(
      res,
      ctx,
      "A registration's email is sent by its create; an edit cannot send one."
    )
  }
  return updateEntityListData(req, res, ctx, getOpportunityRegistrationListData)
}

// resend
//
// The stored email sent once more, as a new `queued` message whose `repeatOf`
// is the ORIGINAL's id (never the previous attempt's); the registration reads
// as that latest attempt. 409 when there is nothing to resend, the supplier
// has answered, or the last attempt has not gone out yet -- see
// resendOpportunityRegistrationEmail.
const resendOpportunityRegistration = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  const { id } = req.params
  const list = await getOpportunityRegistrationListData()
  const registration = list.find((r) => r.id === id)
  const conflict = (detail: string) =>
    res(
      ctx.status(409),
      ctx.json({ status: 409, title: 'Cannot resend', detail })
    )
  if (!registration) {
    return res(ctx.status(404), ctx.json({ status: 404, title: 'Not found' }))
  }
  const previous = registration.registrationEmail
  if (!previous) {
    return conflict('This registration was made manually; it sent no email.')
  }
  if (registration.status?.id !== OpportunityRegistrationStatusIds.SUBMITTED) {
    return conflict('The supplier has answered this registration.')
  }
  if (
    previous.deliveryState === 'queued' ||
    previous.deliveryState === 'sending'
  ) {
    return conflict('This registration email has not been sent yet.')
  }
  const original = previous.repeatOf ?? previous.id
  const message = {
    ...queuedEmailMessage({
      id: `${original}R${Date.now()}`,
      email: {
        toAddresses: previous.toAddresses ?? [],
        ccAddresses: previous.ccAddresses ?? undefined,
        bccAddresses: previous.bccAddresses ?? undefined,
        subject: previous.subject,
        body: previous.body ?? '',
      },
      subjectFallback: registration.descr,
      sentBy: SENT_BY,
    }),
    repeatOf: original,
  }
  registration.registrationEmail = message
  await _storeEntityDataListToLocalForage({
    // storageKey IS the entity key base -- see getListDataFunctionFactory.
    entityKeyBase: 'opportunityregistrations',
    listData: list as never,
    callingFn: '[api/msw/api/opportunityRegistrations] resend |',
  })
  return res(ctx.status(201), ctx.json({ data: message }))
}

const deleteOpportunityRegistration = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  return deleteEntityListData(req, res, ctx, getOpportunityRegistrationListData)
}

export const handlers = [
  {
    method: 'get',
    path: '/api/opportunityregistrations',
    handler: getOpportunityRegistrations,
  },
  {
    method: 'post',
    path: '/api/opportunityregistrations',
    handler: postOpportunityRegistration,
  },
  {
    method: 'post',
    path: '/api/opportunityregistrations/:id/resend',
    handler: resendOpportunityRegistration,
  },
  {
    method: 'patch',
    path: '/api/opportunityregistrations/:id',
    handler: patchOpportunityRegistration,
  },
  {
    method: 'delete',
    path: '/api/opportunityregistrations/:id',
    handler: deleteOpportunityRegistration,
  },
].map((h) => ({ ...h, ...{ handler: requireAuthenticatedUser(h.handler) } }))
